import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, Run, RunFilterOptions, RunQuery } from '../api/client'
import { confirmAndDeleteBatch } from '../utils/batchDelete'
import { fmtDateTime } from '../utils/datetime'
import { theme } from '../theme'
import { useIsAdmin } from '../auth/AuthContext'
import { BulkRescore } from '../components/BulkRescore'
import { ColumnHeader, SortDir } from '../components/ColumnHeader'
import { OutboundInfoButton } from '../components/OutboundInfoButton'
import { Pagination } from '../components/Pagination'
import { QualityCell } from '../components/QualityCell'
import { SlaCell } from '../components/SlaCell'

// The status vocabulary now lives in `utils/runStatus`, and is RE-EXPORTED here because this page
// was its published home. See that module for why it moved.
export { statusColor, statusLabel } from '../utils/runStatus'
import { SLA_LABELS, describeOutboundSettlement, statusColor, statusLabel } from '../utils/runStatus'
import { describeDidAttribution } from '../utils/didAttribution'

/**
 * How this row's stored DID attribution reads, for the loud mark in the ID cell.
 *
 * A thin alias so the call site says what it is doing rather than naming a general describer, and
 * so the marking rule has exactly one entry point on this page.
 */
const didMark = describeDidAttribution
// Aliased for the same reason `didMark` is: the JSX below reads as prose, and the alias is the
// ONE place the listing decides which function answers "does this row need qualifying".
const settlementMark = describeOutboundSettlement

interface Props {
  /** Embedded under the dashboard: no page heading, smaller default page. */
  embedded?: boolean
  /**
   * The window this list must describe, half-open [from, to) in ISO8601 UTC.
   *
   * THE FIX FOR THE RANGE BUG. This component used to own its whole query and take only
   * `embedded`, so the dashboard's time picker had no way to reach it: the picker changed
   * the metrics above and the list below carried on showing every run ever recorded. It is
   * a PROP rather than a second copy of the picker because there must be exactly one
   * selected range on the page — and it carries `DashboardStats.window_from` /
   * `window_to`, the window the BACKEND resolved for the metrics, rather than a range the
   * browser resolves a second time. Two independent resolutions of "last 24 hours" differ
   * by the request skew and by browser-vs-server clock offset, which is unfixable by
   * inspection: the tile and the list would disagree at the edges forever.
   *
   * Absent (the standalone /runs page) means UNBOUNDED — every row, as before.
   */
  window?: { from?: string; to?: string }
  /**
   * Externally-driven SLA drill-through, three-valued exactly as the column filter is:
   * ['breach'] shows only runs whose combined verdict is TRUE. A run with NO configured
   * leg is NULL — not evaluated — and is swept in as neither a breach nor a pass, which
   * is why this passes the same tokens the server already understands instead of a
   * boolean.
   */
  slaFilter?: string[]
  /** Per-leg narrowing, independent of the combined verdict. */
  outboundSlaFilter?: string[]
  inboundSlaFilter?: string[]
}

export default function GlobalRunHistory({
  embedded = false,
  window: rangeWindow,
  slaFilter,
  outboundSlaFilter,
  inboundSlaFilter,
}: Props) {
  const isAdmin = useIsAdmin()
  const [runs, setRuns] = useState<Run[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [options, setOptions] = useState<RunFilterOptions | null>(null)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(embedded ? 25 : 50)

  // Default sort: newest run first, with no filters applied.
  const [sortBy, setSortBy] = useState('scheduled')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const [statusSel, setStatusSel] = useState<string[]>([])
  const [jobSel, setJobSel] = useState<string[]>([])
  const [qualitySel, setQualitySel] = useState<string[]>([])
  const [slaSel, setSlaSel] = useState<string[]>([])
  // Per-leg selections. Driven by the dashboard's drill-through rather than by a column
  // control: "which direction breached" is a different question from "did this breach",
  // and the server has always accepted them as separate parameters.
  const [outboundSlaSel, setOutboundSlaSel] = useState<string[]>([])
  const [inboundSlaSel, setInboundSlaSel] = useState<string[]>([])
  const [tokenGlob, setTokenGlob] = useState('')
  // Debounced copy: typing a glob shouldn't fire a request per keystroke.
  const [tokenQuery, setTokenQuery] = useState('')

  const winFrom = rangeWindow?.from
  const winTo = rangeWindow?.to

  // Adopt the drill-through the host asked for. Keyed on the VALUES, so a re-render with
  // an equal array does not clobber a selection the operator has since made by hand.
  const drillKey = [
    (slaFilter || []).join(','),
    (outboundSlaFilter || []).join(','),
    (inboundSlaFilter || []).join(','),
  ].join('|')
  useEffect(() => {
    setSlaSel(slaFilter || [])
    setOutboundSlaSel(outboundSlaFilter || [])
    setInboundSlaSel(inboundSlaFilter || [])
    setPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillKey])

  // A narrower window invalidates the page number for the same reason a filter does:
  // page 7 of the old result set does not exist in the new one.
  useEffect(() => {
    setPage(1)
  }, [winFrom, winTo])

  useEffect(() => {
    api.getRunFilterOptions().then(setOptions).catch(() => setOptions(null))
  }, [])

  useEffect(() => {
    const t = setTimeout(() => {
      setTokenQuery(tokenGlob)
      setPage(1)
    }, 350)
    return () => clearTimeout(t)
  }, [tokenGlob])

  const query: RunQuery = useMemo(
    () => ({
      status: statusSel.length ? statusSel : undefined,
      job_id: jobSel.length ? jobSel : undefined,
      quality_result: qualitySel.length ? qualitySel : undefined,
      sla: slaSel.length ? slaSel : undefined,
      outbound_sla: outboundSlaSel.length ? outboundSlaSel : undefined,
      inbound_sla: inboundSlaSel.length ? inboundSlaSel : undefined,
      token: tokenQuery || undefined,
      // Sent to the SERVER, never applied in the browser: the table holds one page, so a
      // client-side window would hide rows without pulling matching rows from later pages
      // and the row count under the table would be the count of the wrong population.
      from: winFrom,
      to: winTo,
      sort_by: sortBy,
      sort_dir: sortDir,
      page,
      page_size: pageSize,
    }),
    [
      statusSel, jobSel, qualitySel, slaSel, outboundSlaSel, inboundSlaSel,
      tokenQuery, winFrom, winTo, sortBy, sortDir, page, pageSize,
    ],
  )

  const load = useCallback(() => {
    api
      .listAllRuns(query)
      .then(r => {
        setRuns(r.items)
        setTotal(r.total)
        setTotalPages(r.total_pages)
      })
      .catch(() => { /* transient; the auto-refresh will retry */ })
  }, [query])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    const interval = setInterval(load, 5000)
    return () => clearInterval(interval)
  }, [load])

  const onSort = (key: string, dir: SortDir) => {
    setSortBy(key)
    setSortDir(dir)
    setPage(1)
  }

  // Any filter change invalidates the current page number: page 7 of the old
  // result set is meaningless against a narrower one.
  const filterSetter =
    (set: (v: string[]) => void) =>
    (v: string[]) => { set(v); setPage(1) }

  const activeFilters =
    statusSel.length + jobSel.length + qualitySel.length + slaSel.length +
    outboundSlaSel.length + inboundSlaSel.length + (tokenQuery ? 1 : 0)

  const clearAll = () => {
    setStatusSel([]); setJobSel([]); setQualitySel([]); setSlaSel([])
    setOutboundSlaSel([]); setInboundSlaSel([])
    setTokenGlob(''); setTokenQuery(''); setPage(1)
  }

  const handleDelete = async (runId: string) => {
    if (!window.confirm('Delete this run? This removes the run record and any stored received image.')) return
    try {
      await api.deleteRun(runId)
      load()
    } catch (err: any) {
      window.alert(err.body?.detail || err.message || 'Delete failed')
    }
  }

  /**
   * Delete a whole batch from the listing that shows it.
   *
   * THE SEQUENCE IS NOT HERE, on purpose. `utils/batchDelete` owns the two prompts and the
   * endpoint call, and the Tactical E2E page calls the same function — because the second prompt
   * is an ESCALATION decision (it is what makes `force=true` a choice rather than a default), and
   * two copies of that is one that gets tightened on one screen and stays lax on the other.
   *
   * Whole-batch only, which is why this takes the batch ROW and not an item: a batch's statistics
   * are meaningful only over the population that was actually sent, so removing one fax would
   * shrink the denominator and leave the report describing a different experiment.
   *
   * The label names what is destroyed — the size and the job — because "Delete this batch?" over
   * a row the operator may have scrolled past is not enough to identify what is about to go.
   */
  const [deletingBatch, setDeletingBatch] = useState<string | null>(null)
  const handleDeleteBatch = async (run: Run) => {
    setDeletingBatch(run.id)
    try {
      const outcome = await confirmAndDeleteBatch({
        id: run.id,
        label: `${run.batch_size ?? '?'} faxes on ${run.job_name || run.job_id.slice(0, 8)}`,
      })
      if (outcome.error) {
        window.alert(outcome.error)
        return
      }
      if (outcome.deleted) load()
    } finally {
      setDeletingBatch(null)
    }
  }

  // The Info affordance -- its button, its two panels, and the provider decision behind it -- lives
  // in `components/OutboundInfoButton`, shared with the batch report's item table. It is not
  // documented here any more because it is not implemented here; a comment describing a handler
  // this file no longer has is exactly the "surface that misdescribes the engine" failure, in prose.

  const jobChoices = useMemo(
    () => (options?.jobs || []).map(j => ({ value: j.id, label: j.name })),
    [options],
  )
  const statusChoices = useMemo(
    () => (options?.statuses || []).map(s => ({ value: s, label: statusLabel(s) })),
    [options],
  )
  const qualityChoices = useMemo(
    () => (options?.quality_results || []).map(q => ({ value: q, label: q })),
    [options],
  )
  const slaChoices = useMemo(
    () => (options?.sla || ['pass', 'breach', 'not_evaluated']).map(s => ({
      value: s, label: SLA_LABELS[s] || s,
    })),
    [options],
  )

  const sortProps = { activeSortKey: sortBy, activeSortDir: sortDir, onSort }

  return (
    <div>
      {/* EMBEDDED: NO HEADING AND NO WINDOW LINE (FINISH-LINE A1.2).
          The fact the old heading carried is still true and still matters — an unlabelled
          list under a time picker reads as "all runs", which is how the range bug stayed
          invisible: the numbers moved and the list did not. It is no longer this row's job
          to say so, because the ONLY embedded host is `Dashboard.tsx`, which renders the
          backend-resolved window (`Window <from> → <to>`) directly above these rows and
          passes that same window in as the `window` prop. Two statements of one window,
          one of them derived here, is what was removed — not the label itself.
          If a SECOND host ever embeds this list, it must show the resolved window too, or
          the ambiguity comes straight back. `tests/test_dashboard_range_agreement.py`
          holds the prop contract that makes the Dashboard's copy the authoritative one.

          `flex-end` when embedded: with the heading gone the row's remaining children are
          the filter controls, and space-between would strand `Clear N filters` at the
          left margin. */}
      <div style={{ display: 'flex', justifyContent: embedded ? 'flex-end' : 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        {!embedded && <h2 style={{ margin: 0 }}>Run History — All Jobs</h2>}
        {/* Bulk quality re-measure. Lives here rather than in a host script because the
            app is the only place with both the database and the S3 bucket — it runs on
            Fargate, so the deploy host can reach neither. Counts first, writes second. */}
        {!embedded && isAdmin && <BulkRescore />}
        {activeFilters > 0 && (
          <button
            onClick={clearAll}
            style={{
              padding: '0.35rem 0.7rem', fontSize: '0.8rem', background: theme.bgAlt,
              color: theme.text, border: `1px solid ${theme.border}`, borderRadius: 4, cursor: 'pointer',
            }}
          >
            Clear {activeFilters} filter{activeFilters === 1 ? '' : 's'}
          </button>
        )}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', background: theme.bgAlt, borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
        <thead>
          <tr style={{ background: theme.bgElevated, textAlign: 'left' }}>
            <th style={thStyle}>
              <ColumnHeader label="Job" sortKey="job" {...sortProps}
                choices={jobChoices} selected={jobSel} onSelect={filterSetter(setJobSel)} />
            </th>
            <th style={thStyle}>
              <ColumnHeader label="E2E Run ID" sortKey="run_token" {...sortProps}
                globValue={tokenGlob} onGlob={setTokenGlob} />
            </th>
            <th style={thStyle}>
              <ColumnHeader label="Status" sortKey="status" {...sortProps}
                choices={statusChoices} selected={statusSel} onSelect={filterSetter(setStatusSel)} />
            </th>
            <th style={thStyle}>
              <ColumnHeader label="Submitted" sortKey="submitted" {...sortProps} />
            </th>
            <th style={thStyle}>
              <ColumnHeader label="Combined E2E Time" sortKey="e2e_time" {...sortProps} />
            </th>
            <th style={thStyle}>
              {/* THE FILTER AND SORT UNDER THIS HEADER ARE THE OTHER AXIS, AND THAT IS NOT
                  FIXED HERE. The CELL now shows the quality SCORE and its stored SLA verdict,
                  while `sortKey="quality"` maps to `H.c.quality_result` in the backend's sort
                  map and the chips come from `quality_results` — both the OCR readability enum.
                  Filtering to `degraded` is still a coherent question, it is just a different
                  one from the number in the cell. Left alone deliberately: changing the sort
                  key and the filter vocabulary means touching the endpoint's filter map and
                  the options endpoint, which is a separate change with its own guards. */}
              <ColumnHeader label="Quality" sortKey="quality" {...sortProps}
                choices={qualityChoices} selected={qualitySel} onSelect={filterSetter(setQualitySel)} />
            </th>
            <th style={thStyle}>
              <ColumnHeader label="SLA" sortKey="sla" {...sortProps}
                choices={slaChoices} selected={slaSel} onSelect={filterSetter(setSlaSel)} />
            </th>
            <th style={thStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {runs.map(run => (
            <tr key={run.id} style={{ borderTop: `1px solid ${theme.border}` }}>
              <td style={tdStyle}>
                <Link to={`/jobs/${run.job_id}/runs`} style={{ color: theme.primary, textDecoration: 'none', fontWeight: 500, fontSize: '0.85rem' }}>
                  {run.job_name || run.job_id}
                </Link>
              </td>
              <td style={tdStyle}>
                {/* A batch is ONE row here regardless of size, and its click must go to the
                    batch report: a batch id is not a run id, so the run route would 404. The
                    badge exists so a reader is never left inferring the kind from a blank
                    token cell. */}
                {run.kind === 'batch' ? (
                  <Link to={`/tactical/${run.id}`} style={{ color: theme.primary, textDecoration: 'none', fontSize: '0.8rem' }}>
                    <span style={{
                      background: theme.primary + '22', border: `1px solid ${theme.primary}55`,
                      borderRadius: 3, padding: '0.05rem 0.35rem', fontSize: '0.68rem',
                      fontWeight: 600, marginRight: '0.4rem',
                    }}>BATCH</span>
                    {run.batch_size} faxes
                  </Link>
                ) : (
                  <>
                    <Link to={`/runs/${run.id}`} style={{ color: theme.text, textDecoration: 'none', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {run.run_token}
                    </Link>
                    {/* WHERE THE ARRIVAL DID CAME FROM (DID-AUTHORITY-LADDER), ON EVERY ROW THAT
                        RECORDED ONE. The operator's ruling: *"I dont think we need to display a
                        WARNING, but we should be clear about where the authoritative timestamp came
                        from."* So this is no longer PR #174's amber badge on the subset we judged
                        shaky — it is a quiet, always-present statement of WHICH source, in the same
                        two-or-three-word vocabulary run detail uses.

                        WHY ALWAYS BEATS ONLY-WHEN-SHAKY, beyond the ruling: a mark that appears
                        only when we distrust a value makes its ABSENCE mean two things at once, and
                        under #174 an unmarked row was either a rung-1 attribution or a row with
                        nothing recorded at all. Now the chip is present for every recorded
                        attribution, so an empty cell means exactly one thing — NOT RECORDED, the
                        permanent answer for every fax measured before the ladder stored its
                        decision. Naming a source there would invent a measurement (Decision 2).

                        IT SITS IN THE ID CELL rather than a new column, so it is beside the
                        identity of the fax it qualifies and a listing already ten columns wide
                        gains none. A CONFLICT is the one case still coloured: comparable rungs
                        disagreeing is a fault in the world, and declining warnings is not declining
                        error reporting. */}
                    {didMark(run).sourceShort && (
                      <span
                        title={didMark(run).text}
                        style={{
                          marginLeft: '0.4rem',
                          background: didMark(run).fault ? '#3a1212' : 'transparent',
                          border: `1px solid ${didMark(run).fault ? '#7f2f2f' : theme.border}`,
                          color: didMark(run).fault ? '#ef9a9a' : theme.textMuted,
                          borderRadius: 3, padding: '0.05rem 0.35rem',
                          fontSize: '0.66rem', fontWeight: didMark(run).fault ? 700 : 500,
                          cursor: 'help', whiteSpace: 'nowrap',
                        }}
                      >
                        {didMark(run).sourceShort}
                      </span>
                    )}
                  </>
                )}
              </td>
              <td style={tdStyle}>
                <span
                  title={run.quality_detail || ''}
                  style={{ color: statusColor(run.status), fontWeight: 500, fontSize: '0.85rem', cursor: run.quality_detail ? 'help' : 'default' }}
                >
                  {statusLabel(run.status)}{run.quality_detail && ['send_failed', 'receive_unreadable', 'manually_failed'].includes(run.status) ? ' ⓘ' : ''}
                </span>
                {/* `Sending` MEANS TWO DIFFERENT THINGS AND THEY NEED OPPOSITE RESPONSES.
                    A leg whose dispatch was lost is stuck and wants intervention; a leg for
                    which nothing will EVER report a completion — SRFax's fax-by-email gateway
                    returns no handle for `Get_FaxStatus` to be keyed on — is RESTING and wants
                    patience. The stuck-send sweep already tells them apart (it skips the
                    second, because `t_submitted` is set and the row is correct); until this
                    badge, an operator could not, and reading a resting leg as a fault produced
                    a wrong diagnosis.

                    IT SITS BESIDE THE STATUS IT QUALIFIES rather than in a new column, exactly
                    as the DID mark sits beside the identity it qualifies, and a listing already
                    ten columns wide gains none.

                    WHICH ROWS: `describeOutboundSettlement` alone decides, from the backend's
                    `outbound_self_settling` — the same field run detail and the Info panel
                    read, so the three cannot disagree. It marks only rows actually IN
                    `sending`: on a settled row the answer is history, and badging every
                    terminal row of an SMTP job would train the eye to skip the badge. */}
                {settlementMark(run).mark && (
                  <div
                    title={settlementMark(run).text}
                    style={{
                      marginTop: '0.2rem', color: '#9aa0a6', fontSize: '0.66rem',
                      fontWeight: 600, cursor: 'help', whiteSpace: 'nowrap',
                    }}
                  >
                    {settlementMark(run).badge}
                  </div>
                )}
              </td>
              <td style={{ ...tdStyle, fontSize: '0.8rem' }}>{fmtDateTime(run.t_submitted)}</td>
              <td style={tdStyle}>{run.e2e_duration_seconds ? `${run.e2e_duration_seconds.toFixed(1)}s` : '—'}</td>
              <td style={tdStyle}>
                {/* THE QUALITY SLA COLUMN: the SCORE, coloured from the STORED verdict.
                    See `components/QualityCell` and `utils/qualityCellState` for the rule.

                    WHAT WAS HERE: `run.quality_result` — the OCR readability enum
                    (pass/degraded/fail) — coloured green/amber/red with no threshold consulted
                    at all. That is a DIFFERENT AXIS from the quality SLA: `_quality_check`
                    answers "did this arrive readable with the expected page count", the SLA
                    answers "did it score at least the job's minimum". A fax scoring 12 against
                    a minimum of 70 read as a green `pass`. The score, the effective minimum and
                    the stored verdict were all on the wire the whole time — the `Run` interface
                    just did not declare them, so the component could not see them. */}
                <QualityCell run={run} />
              </td>
              <td style={tdStyle}><SlaCell run={run} /></td>
              <td style={tdStyle}>
                {/* A BATCH row gets neither of the run actions, and that is not cosmetic: both
                    call /api/runs/{id}/… endpoints, and a batch id is not a run id — clicking
                    Info on a batch row produced an error popup.

                    NO REPORT BUTTON, AND THIS WAS CHECKED BEFORE IT WENT (FINISH-LINE A1.3).
                    There used to be a `Report` link here, styled like the run buttons, which in a
                    row of destructive-looking controls read as a delete. It was REMOVED, not
                    relocated, because the route it opened is already reachable from this same row:
                    the E2E Run ID cell above renders `<Link to={`/tactical/${run.id}`}>` for a
                    batch — the SAME path, the SAME id, resolving to the SAME
                    `<Route path="/tactical/:id" element={<BatchReport />} />` in `App.tsx`. Two
                    links to one route, so nothing was lost with one gone.
                    `tests/test_batch_dispatch.py::test_batch_row_links_to_the_report` parses the
                    ID cell's batch branch and fails if that link ever leaves the row, which is
                    what would turn this into "we deleted the only path to the report".

                    WHAT IS HERE NOW IS DELETE, which is what the operator asked for: a batch
                    listing has to be removable from the listing that shows it. Admin-gated on the
                    CLIENT because a control that can only 403 is its own defect, and refused on
                    the SERVER regardless — `auth/middleware.py` makes every `/api/` write
                    admin-only, so this button's absence is presentation and not the enforcement. */}
                {run.kind === 'batch' ? (
                  isAdmin ? (
                    <button
                      onClick={() => handleDeleteBatch(run)}
                      disabled={deletingBatch === run.id}
                      title="Delete this batch and every fax in it"
                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', background: theme.bgAlt, color: '#ef5350', border: '1px solid #5a2a2a', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      {deletingBatch === run.id ? '…' : 'Delete'}
                    </button>
                  ) : (
                    // The em dash keeps the cell from reading as a failed render.
                    <span style={{ color: theme.textMuted, fontSize: '0.8rem' }}>—</span>
                  )
                ) : (
                  <>
                {/* ONE Info implementation, shared with the batch report's item table. The click
                    passes the row's id and NOTHING else: the endpoint resolves that leg's provider,
                    so no caller can reintroduce the dispatch on `efax_fax_id` that once routed
                    every SFax run to eFax metadata. See `components/OutboundInfoButton`. */}
                <OutboundInfoButton transmissionId={run.id} onRefreshed={load} />
                {!['in_flight', 'sending'].includes(run.status) && isAdmin && (
                  <button
                    onClick={() => handleDelete(run.id)}
                    title="Delete this run"
                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', background: theme.bgAlt, color: '#ef5350', border: '1px solid #5a2a2a', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Delete
                  </button>
                )}
                  </>
                )}
              </td>
            </tr>
          ))}
          {runs.length === 0 && (
            <tr>
              <td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted }}>
                {activeFilters > 0
                  ? 'No runs match the active filters in this window.'
                  : winFrom && winTo
                    ? 'No runs in the selected time range.'
                    : 'No runs found.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={pageSize}
        onPage={setPage}
        onPageSize={s => { setPageSize(s); setPage(1) }}
      />

    </div>
  )
}

const thStyle: React.CSSProperties = { padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#9aa0a6' }
const tdStyle: React.CSSProperties = { padding: '0.5rem 0.75rem' }

